/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { type Insight as BaseInsight, registerStandardChartInsight } from '../insightManager';
import { toArray } from '../../util/data';

type InsightData = {
  /** 要标记的 x 轴指标起始数据值 */
  xStart: string;
  /** 要标记的 x 轴指标终止数据值 */
  xEnd: string;
  /** 要标记的 x 轴指标起始文本描述 */
  xStartSummary?: string;
  /** 要标记的 x 轴指标终止文本描述 */
  xEndSummary?: string;
  /** 边界线 @see https://echarts.apache.org/zh/option.html#series-bar.markLine.data */
  line?: unknown;
  /** 边界线 @see https://echarts.apache.org/zh/option.html#series-bar.markArea.data */
  area?: unknown;
  /** 出现动画时长，时长为 0 时关闭动画 */
  animationDuration?: number;
};

const type = 'IntervalX';
const INSIGHT_FLAG = `insight_${type}`;

interface Insight extends BaseInsight<InsightData[]> {
  readonly type: typeof type;
}

registerStandardChartInsight<Insight>({
  type,
  filter: option => {
    return (
      toArray(option.xAxis).length > 0 &&
      toArray(option.yAxis).length > 0 &&
      !toArray(option.series).some(item => !['line', 'bar', 'scatter'].includes(item.type))
    );
  },
  clear: {
    onOptionUpdate(option) {
      option.series = toArray(option.series).map(series => {
        series.markLine = series.markLine || {};
        series.markLine.data = (series.markLine.data || []).filter(
          item => item.__insightFlag !== INSIGHT_FLAG
        );

        series.markArea = series.markArea || {};
        series.markArea.data = (series.markArea.data || []).filter(
          item => item.__insightFlag !== INSIGHT_FLAG
        );

        return series;
      });
    }
  },
  /** @see https://datav.iwencai.com/example.html#/static-page?id=252 */
  render: {
    onOptionUpdate(context, option) {
      const insight = context.insightOption;
      function render(data: InsightData) {
        const { xStart, xEnd, xStartSummary = '', xEndSummary = '', line: lineOpt = {} } = data;
        const series = context.util.data.toArray(option.series)[0];

        series.markLine = series.markLine || {};
        series.markLine.data = series.markLine.data || [];

        const startLine = {
          ...context.util.data.deepMerge(lineOpt, {
            // ! bugs @see https://github.com/apache/echarts/issues/18433
            symbol: 'none',
            dvSymbolNone: true,
            label: {
              position: 'insideStartTop'
            }
          }),
          // ! name 在该模式下不生效
          name: xStartSummary,
          dvLabelText: xStartSummary,
          xAxis: xStart,
          __insightFlag: INSIGHT_FLAG
        };

        const endLine = {
          ...context.util.data.deepMerge(lineOpt, {
            // ! bugs @see https://github.com/apache/echarts/issues/18433
            symbol: 'none',
            dvSymbolNone: true,
            label: {
              position: 'insideStartTop'
            }
          }),
          // ! name 在该模式下不生效
          name: xEndSummary,
          dvLabelText: xEndSummary,
          xAxis: xEnd,
          __insightFlag: INSIGHT_FLAG
        };

        series.markLine.data.push(startLine, endLine);
      }

      function renderArea(data: InsightData) {
        const { xStart, xEnd, area: areaOpt = {} } = data;
        const series = context.util.data.toArray(option.series)[0];

        series.markArea = series.markArea || {};
        series.markArea.data = series.markArea.data || [];

        const area = [
          {
            ...(areaOpt as any),
            xAxis: xStart
          },
          {
            ...(areaOpt as any),
            xAxis: xEnd
          }
        ];
        area.__insightFlag = INSIGHT_FLAG;

        series.markArea.data.push(area);
      }

      insight.data.forEach(item => {
        render(item);
        renderArea(item);
      });
    }
  }
});
